import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { cn } from '../../../components/ui/utils';
import { PlatformIcon, useAccountStore } from '../../../entities/account';
import { accountsApi } from '../../../shared/api/accounts.api';
import { partnerApi } from '../../../shared/api/partner.api';
import { invalidateTikTokAccounts } from '../../../shared/api/tiktok.api';
import { youtubeApi, type YouTubeChannelRecord } from '../../../shared/api/youtube.api';
import {
  INSTAGRAM_REDIRECT_URI, InstagramLoginClosed, instagramLoginCode, PARTNER_SCOPES, STAFF_SCOPES,
} from '../lib/instagramLogin';
import { popupLogin, PopupLoginClosed, POPUP_NETWORK_LABEL, STAFF_AUTH_URL, type PopupNetwork } from '../lib/popupLogin';
import { LoginPopupHint } from './LoginPopupHint';

export type ConnectPlatform = 'instagram' | 'tiktok' | 'youtube' | 'threads';

/** Кто подключает: сотрудник — наш паблик со всеми правами, партнёр — свой, только статистика. */
export type ConnectAudience = 'staff' | 'partner';

const PLATFORMS: { id: ConnectPlatform; label: string; method: string }[] = [
  { id: 'instagram', label: 'Instagram', method: 'вход в Instagram' },
  { id: 'tiktok', label: 'TikTok', method: 'вход в TikTok' },
  { id: 'youtube', label: 'YouTube', method: 'ссылка на канал' },
  { id: 'threads', label: 'Threads', method: 'вход в Threads' },
];

/**
 * Подсказки партнёру: короткая — сразу в плитке, полная — после ошибки этой
 * сети. Раньше всё это висело одной стеной текста под четырьмя кнопками.
 */
const PARTNER_HINTS: Record<ConnectPlatform, { short: string; full: ReactNode }> = {
  instagram: {
    short: 'Только профессиональный аккаунт — «Автор» или «Бизнес»',
    full: (
      <>
        <p>Аккаунт должен быть профессиональным — «Автор» или «Бизнес». Личный аккаунт Instagram не подключится.</p>
        <p>
          Если Instagram пишет, что у вас нет доступа к приложению, сначала примите наше приглашение на сайте Instagram:{' '}
          <a href="https://www.instagram.com/accounts/manage_access/" target="_blank" rel="noreferrer" className="underline underline-offset-2">
            «Приложения и сайты» → «Приглашения тестировщика»
          </a>
          {' '}— и подключите аккаунт ещё раз.
        </p>
      </>
    ),
  },
  tiktok: {
    short: 'Пишет «приложение недоступно» — пришлите нам ник',
    full: <p>TikTok пишет, что приложение недоступно, — напишите нам ваш ник в TikTok: мы откроем доступ, и подключите ещё раз.</p>,
  },
  youtube: {
    short: 'Входить в Google не нужно — видим только публичные цифры',
    full: <p>YouTube подключается ссылкой на канал или любое его видео — входить в Google не нужно: мы видим только публичные цифры.</p>,
  },
  threads: {
    short: 'Сначала примите наше приглашение в Threads',
    full: <p>Threads пишет, что нет доступа, — сначала примите наше приглашение в приложении Threads: «Настройки» → «Аккаунт» → «Разрешения для сайтов» → «Приглашения».</p>,
  },
};

const NEW_CHANNEL = 'new';

interface ConnectAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audience: ConnectAudience;
  /**
   * Вход закончился: 'connected' — сеть сказала, что подключено; 'closed' —
   * окно закрыли без ответа (ответ мог не дойти с другого домена). В обоих
   * случаях список стоит перечитать.
   */
  onConnected?: (platform: ConnectPlatform, outcome: 'connected' | 'closed') => void | Promise<void>;
}

/**
 * Одна кнопка «Добавить аккаунт» вместо четырёх иконок без подписей (02.11.2026):
 * окно с четырьмя плитками, у каждой — способ подключения одной строкой.
 * YouTube — ссылкой прямо здесь же, остальные — окном входа сети.
 */
export function ConnectAccountDialog({ open, onOpenChange, audience, onConnected }: ConnectAccountDialogProps) {
  const addAccount = useAccountStore((state) => state.addAccount);
  const [busy, setBusy] = useState<ConnectPlatform | null>(null);
  /** Сеть, чьё окно входа сейчас открыто, — для подсказки «окно спряталось». */
  const [popupNetwork, setPopupNetwork] = useState<string | null>(null);
  const [error, setError] = useState<{ platform: ConnectPlatform; message: string } | null>(null);
  const [youtubeOpen, setYoutubeOpen] = useState(false);
  const [link, setLink] = useState('');
  const [target, setTarget] = useState(NEW_CHANNEL);
  const [channels, setChannels] = useState<YouTubeChannelRecord[] | null>(null);

  // Каждое открытие — с чистого листа: прошлая ошибка к новой попытке не относится.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setYoutubeOpen(false);
    setLink('');
    setTarget(NEW_CHANNEL);
  }, [open]);

  const partner = audience === 'partner';

  const finish = async (platform: ConnectPlatform) => {
    onOpenChange(false);
    await onConnected?.(platform, 'connected');
  };

  const connectInstagram = async () => {
    setBusy('instagram');
    setError(null);
    try {
      setPopupNetwork('Instagram');
      const code = await instagramLoginCode(partner ? PARTNER_SCOPES : STAFF_SCOPES).finally(() => setPopupNetwork(null));
      if (partner) {
        const { account, reconnected } = await partnerApi.connectInstagram(code, INSTAGRAM_REDIRECT_URI);
        toast.success(reconnected ? `@${account.username} переподключён` : `@${account.username} подключён`);
      } else {
        const response = await accountsApi.createFromOAuth({ code, redirectUri: INSTAGRAM_REDIRECT_URI });
        addAccount(response.account);
        toast.success(`Аккаунт @${response.account.username} подключён`);
      }
      await finish('instagram');
    } catch (err) {
      if (!(err instanceof InstagramLoginClosed)) {
        setError({ platform: 'instagram', message: err instanceof Error ? err.message : 'Не удалось подключить Instagram' });
      }
    } finally {
      setBusy(null);
    }
  };

  const connectPopup = async (network: PopupNetwork) => {
    setBusy(network);
    setError(null);
    const label = POPUP_NETWORK_LABEL[network];
    try {
      setPopupNetwork(label);
      const url = partner
        ? (network === 'tiktok' ? partnerApi.tiktokAuthUrl : partnerApi.threadsAuthUrl)
        : STAFF_AUTH_URL[network];
      const { name } = await popupLogin(network, url).finally(() => setPopupNetwork(null));
      // Список TikTok кэшируется на время жизни вкладки — сбрасываем, иначе
      // новый аккаунт не появится до перезагрузки страницы.
      if (network === 'tiktok') invalidateTikTokAccounts();
      toast.success(`${label} подключён${name ? `: @${name}` : ''}`);
      await finish(network);
    } catch (err) {
      if (err instanceof PopupLoginClosed) {
        if (network === 'tiktok') invalidateTikTokAccounts();
        await onConnected?.(network, 'closed');
      } else {
        setError({ platform: network, message: err instanceof Error ? err.message : `Не удалось подключить ${label}` });
      }
    } finally {
      setBusy(null);
    }
  };

  const openYouTube = () => {
    setError(null);
    setYoutubeOpen(true);
    if (partner) return;
    // Каналы, заведённые для «Монтажа» одним именем, можно выбрать — ссылка
    // приклеится к ним, и задачи на канал не порвутся.
    setChannels(null);
    youtubeApi.channels()
      .then(({ channels }) => setChannels(channels.sort((a, b) => a.name.localeCompare(b.name, 'ru'))))
      .catch((err) => { setChannels([]); toast.error(err.message); });
  };

  const submitYouTube = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('youtube');
    setError(null);
    try {
      const { channel, created } = partner
        ? await youtubeApi.attachPartner(link.trim())
        : await youtubeApi.attach(link.trim(), target === NEW_CHANNEL ? undefined : target);
      toast.success(created
        ? `Канал «${channel.name}» подключён`
        : partner ? `Канал «${channel.name}» уже подключён` : `Ссылка у канала «${channel.name}» сохранена`);
      await finish('youtube');
    } catch (err) {
      setError({ platform: 'youtube', message: err instanceof Error ? err.message : 'Не удалось подключить канал' });
    } finally {
      setBusy(null);
    }
  };

  const start = (platform: ConnectPlatform) => {
    if (platform === 'instagram') void connectInstagram();
    else if (platform === 'youtube') openYouTube();
    else void connectPopup(platform);
  };

  const title = partner ? 'Подключить аккаунт' : 'Добавить аккаунт';
  const errorBlock = error && (
    <div className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
      <p className="font-medium text-destructive">{error.message}</p>
      {partner && <div className="space-y-1.5 text-xs text-muted-foreground">{PARTNER_HINTS[error.platform].full}</div>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{youtubeOpen ? 'YouTube-канал' : title}</DialogTitle>
          <DialogDescription>
            {youtubeOpen
              ? 'Ссылка на канал или любое его видео. Доступ к каналу не нужен: просмотры, видео и подписчики — публичные, их снимаем раз в сутки.'
              : partner
                ? 'Публиковать от вашего имени и читать сообщения мы не можем — доступ только к профилю и статистике.'
                : 'Выберите сеть — откроется её окно входа. YouTube подключается ссылкой.'}
          </DialogDescription>
        </DialogHeader>

        {youtubeOpen ? (
          <form onSubmit={submitYouTube} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="connect-yt-link">Ссылка</Label>
              <Input
                id="connect-yt-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://www.youtube.com/@канал"
                required
                autoFocus
              />
            </div>
            {!partner && (
              <div className="space-y-1.5">
                <Label>Канал в InstaFarm</Label>
                <Select value={target} onValueChange={setTarget} disabled={!channels}>
                  <SelectTrigger><SelectValue placeholder="Загружаем…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NEW_CHANNEL}>Новый канал</SelectItem>
                    {(channels ?? []).filter((c) => !c.partnerUserId).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.channelId ? ' — ссылка есть' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Канал уже заведён для «Монтажа» — выберите его, чтобы не появился дубль.</p>
              </div>
            )}
            {errorBlock}
            <div className="flex justify-between gap-2">
              <Button type="button" variant="ghost" onClick={() => { setYoutubeOpen(false); setError(null); }}>
                <ArrowLeft />
                Другая сеть
              </Button>
              <Button type="submit" disabled={busy !== null || !link.trim()}>
                {busy === 'youtube' && <Loader2 className="animate-spin" />}
                Подключить
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => start(p.id)}
                  disabled={busy !== null}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                    'hover:bg-accent disabled:pointer-events-none disabled:opacity-60',
                    error?.platform === p.id && 'border-destructive/40',
                  )}
                >
                  <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                    {busy === p.id ? <Loader2 className="size-4 animate-spin" /> : <PlatformIcon platform={p.id} className="size-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{p.label}</span>
                    <span className="block text-xs text-muted-foreground">{p.method}</span>
                    {partner && <span className="mt-1 block text-xs text-muted-foreground">{PARTNER_HINTS[p.id].short}</span>}
                  </span>
                </button>
              ))}
            </div>
            {popupNetwork && <LoginPopupHint network={popupNetwork} />}
            {errorBlock}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
